import { useState, useEffect } from "react";

const timeSlots = [
  "All Day",
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
];

const STORAGE_KEY = "appointment-calendar-view";

export function AppointmentCalendar({ appointments }) {
  const [viewMode, setViewMode] = useState("Week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoaded, setIsLoaded] = useState(false);

  // Load view preference from localStorage on mount
  useEffect(() => {
    const savedView = localStorage.getItem(STORAGE_KEY);
    if (savedView && ["Day", "Week", "Month"].includes(savedView)) {
      setViewMode(savedView);
    }
    setIsLoaded(true);
  }, []);

  // Save view preference to localStorage when it changes
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, viewMode);
    }
  }, [viewMode, isLoaded]);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const fullDayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d;
  };

  const getWeekDays = (startDate) => {
    const days = [];
    const start = getWeekStart(startDate);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add empty slots for days before first of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const weekDays = getWeekDays(currentDate);
  const monthDays = getMonthDays(currentDate);

  const formatDateRange = (startDate) => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${shortMonths[startDate.getMonth()]} ${startDate.getDate()}-${endDate.getDate()}, ${startDate.getFullYear()}`;
    }
    return `${shortMonths[startDate.getMonth()]} ${startDate.getDate()} - ${shortMonths[endDate.getMonth()]} ${endDate.getDate()}, ${startDate.getFullYear()}`;
  };

  const formatDayHeader = (date) => {
    return `${fullDayNames[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const formatMonthHeader = (date) => {
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === "Day") {
      newDate.setDate(currentDate.getDate() + (direction === "next" ? 1 : -1));
    } else if (viewMode === "Week") {
      newDate.setDate(currentDate.getDate() + (direction === "next" ? 7 : -7));
    } else {
      newDate.setMonth(
        currentDate.getMonth() + (direction === "next" ? 1 : -1),
      );
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getAppointmentsForCell = (dayIndex, hour) => {
    return appointments.filter(
      (apt) => apt.day === dayIndex && apt.hour === hour,
    );
  };

  const getAppointmentsForDate = (date, hour) => {
    return appointments.filter((apt) => {
      if (apt.date) {
        const aptDate = new Date(apt.date);
        const sameDay =
          aptDate.getDate() === date.getDate() &&
          aptDate.getMonth() === date.getMonth() &&
          aptDate.getFullYear() === date.getFullYear();
        return hour !== undefined ? sameDay && apt.hour === hour : sameDay;
      }
      // Fallback for appointments using day index (0-6 for the current week)
      const weekStart = getWeekStart(currentDate);
      const aptDayDate = new Date(weekStart);
      aptDayDate.setDate(weekStart.getDate() + apt.day);
      const sameDay =
        aptDayDate.getDate() === date.getDate() &&
        aptDayDate.getMonth() === date.getMonth() &&
        aptDayDate.getFullYear() === date.getFullYear();
      return hour !== undefined ? sameDay && apt.hour === hour : sameDay;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  const handleViewChange = (mode) => {
    setViewMode(mode);
  };

  const getHeaderText = () => {
    if (viewMode === "Day") {
      return formatDayHeader(currentDate);
    } else if (viewMode === "Week") {
      return formatDateRange(getWeekStart(currentDate));
    } else {
      return formatMonthHeader(currentDate);
    }
  };

  const cn = (...classes) => {
    return classes.filter(Boolean).join(" ");
  };

  // Don't render until we've loaded the saved preference
  if (!isLoaded) {
    return (
      <div className="calendar-container">
        <div className="animate-pulse">
          <div className="loading-title"></div>
          <div className="loading-content"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2 className="calendar-title">Appointments Calendar</h2>

        {/* Calendar Controls */}
        <div className="calendar-controls">
          <div className="date-navigation">
            <button className="today-btn" onClick={goToToday}>
              Today
            </button>
            <button className="nav-btn" onClick={() => navigate("prev")}>
              <svg
                className="nav-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button className="nav-btn" onClick={() => navigate("next")}>
              <svg
                className="nav-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <span className="current-date">{getHeaderText()}</span>
          </div>
          <div className="view-controls">
            <div className="view-toggle">
              {["Day", "Week", "Month"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleViewChange(mode)}
                  className={cn(
                    "view-toggle-btn",
                    viewMode === mode ? "view-toggle-btn-active" : "",
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
            <button className="grid-btn">
              <svg
                className="grid-icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Day View */}
      {viewMode === "Day" && (
        <div className="calendar-scroll-container">
          <div className="day-view">
            {/* Day Header */}
            <div className="day-header-grid">
              <div className="time-label-header"></div>
              <div className="day-header-cell">
                <div className="day-name">{dayNames[currentDate.getDay()]}</div>
                <div
                  className={cn(
                    "day-number",
                    isToday(currentDate) ? "today-highlight" : "",
                  )}
                >
                  {currentDate.getDate()}
                </div>
              </div>
            </div>

            {/* Time Slots */}
            {timeSlots.map((time, timeIndex) => {
              const dayAppointments = getAppointmentsForDate(
                currentDate,
                timeIndex,
              );
              return (
                <div key={time} className="time-row">
                  <div className="time-label">{time}</div>
                  <div className="time-slot-cell">
                    {dayAppointments.map((apt) => (
                      <div key={apt.id} className="appointment-item">
                        <div className="appointment-header">
                          <span className="appointment-time">{apt.time}</span>
                          <span className="appointment-badge">D</span>
                        </div>
                        <div className="appointment-client">
                          {apt.clientName}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {viewMode === "Week" && (
        <div className="calendar-scroll-container">
          <div className="week-view">
            {/* Header Row */}
            <div className="week-header-grid">
              <div className="time-label-header"></div>
              {weekDays.map((date, index) => (
                <div
                  key={index}
                  className={cn(
                    "week-header-cell",
                    isWeekend(date) && "weekend-cell",
                  )}
                >
                  <div className="week-day-name">{dayNames[date.getDay()]}</div>
                  <div
                    className={cn(
                      "week-day-number",
                      isToday(date)
                        ? "today-highlight"
                        : isWeekend(date)
                          ? "weekend-text"
                          : "",
                    )}
                  >
                    {date.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Slots */}
            {timeSlots.map((time, timeIndex) => (
              <div key={time} className="week-time-row">
                <div className="time-label">{time}</div>
                {weekDays.map((date, dayIndex) => {
                  const cellAppointments = getAppointmentsForCell(
                    dayIndex,
                    timeIndex,
                  );
                  return (
                    <div
                      key={dayIndex}
                      className={cn(
                        "week-slot-cell",
                        isWeekend(date) && "weekend-slot",
                      )}
                    >
                      {cellAppointments.map((apt) => (
                        <div key={apt.id} className="week-appointment-item">
                          <div className="week-appointment-header">
                            <span className="week-appointment-time">
                              {apt.time}
                            </span>
                            <span className="week-appointment-badge">D</span>
                          </div>
                          <div className="week-appointment-client">
                            {apt.clientName}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month View */}
      {viewMode === "Month" && (
        <div className="month-view-container">
          {/* Day Names Header */}
          <div className="month-day-names">
            {dayNames.map((day) => (
              <div key={day} className="month-day-name">
                {day}
              </div>
            ))}
          </div>

          {/* Month Grid */}
          <div className="month-grid">
            {monthDays.map((date, index) => {
              const dayAppointments = date ? getAppointmentsForDate(date) : [];
              const isCurrentMonth =
                date?.getMonth() === currentDate.getMonth();

              return (
                <div
                  key={index}
                  className={cn(
                    "month-day-cell",
                    !date && "empty-day",
                    date && isWeekend(date) && "weekend-day",
                    date && isToday(date) && "today-day",
                  )}
                >
                  {date && (
                    <>
                      <div
                        className={cn(
                          "month-day-number",
                          isToday(date)
                            ? "today-highlight-month"
                            : isCurrentMonth
                              ? "current-month-day"
                              : "other-month-day",
                        )}
                      >
                        {date.getDate()}
                      </div>
                      <div className="month-appointments">
                        {dayAppointments.slice(0, 2).map((apt) => (
                          <div key={apt.id} className="month-appointment-item">
                            <span className="month-appointment-time">
                              {apt.time}
                            </span>{" "}
                            <span className="month-appointment-client">
                              {apt.clientName}
                            </span>
                          </div>
                        ))}
                        {dayAppointments.length > 2 && (
                          <div className="more-appointments">
                            +{dayAppointments.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style jsx>{`
        .calendar-container {
          border-radius: 12px;
          border: 1px solid var(--light-gray);
          background-color: var(--background);
          overflow: hidden;
          color: var(--text);
        }

        .calendar-header {
          padding: 16px;
          border-bottom: 1px solid var(--light-gray);
        }

        .calendar-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 16px;
        }

        .calendar-controls {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        @media (min-width: 640px) {
          .calendar-controls {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        }

        .date-navigation {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .today-btn {
          padding: 6px 12px;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          border: 1px solid var(--light-gray);
          background-color: var(--background);
          color: var(--text);
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .today-btn:hover {
          background-color: var(--light-gray);
        }

        .nav-btn {
          padding: 6px;
          border-radius: 6px;
          border: none;
          background: none;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .nav-btn:hover {
          background-color: var(--light-gray);
        }

        .nav-icon {
          color: var(--icon);
        }

        .current-date {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          margin-left: 8px;
        }

        .view-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .view-toggle {
          display: inline-flex;
          border-radius: 8px;
          border: 1px solid var(--light-gray);
          background-color: var(--light-gray);
          padding: 2px;
        }

        .view-toggle-btn {
          padding: 6px 12px;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          border: none;
          background: none;
          color: var(--gray);
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle-btn:hover {
          color: var(--text);
        }

        .view-toggle-btn-active {
          background-color: var(--background);
          color: var(--text);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .grid-btn {
          padding: 8px;
          border-radius: 6px;
          border: 1px solid var(--light-gray);
          background: none;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .grid-btn:hover {
          background-color: var(--light-gray);
        }

        .grid-icon {
          color: var(--icon);
        }

        /* Loading Styles */
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .loading-title {
          height: 32px;
          width: 192px;
          border-radius: 4px;
          background-color: var(--light-gray);
          margin-bottom: 16px;
        }

        .loading-content {
          height: 384px;
          border-radius: 8px;
          background-color: var(--light-gray);
        }

        /* Day View Styles */
        .calendar-scroll-container {
          overflow: auto;
          max-height: 600px;
        }

        .day-view {
          min-width: 300px;
        }

        .day-header-grid {
          display: grid;
          grid-template-columns: 80px 1fr;
          border-bottom: 1px solid var(--light-gray);
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
        }

        .time-label-header {
          padding: 12px;
          font-size: 14px;
          font-weight: 500;
          color: var(--gray);
        }

        .day-header-cell {
          padding: 12px;
          text-align: center;
          border-left: 1px solid var(--light-gray);
        }

        .day-name {
          font-size: 12px;
          font-weight: 500;
          color: var(--gray);
          text-transform: uppercase;
        }

        .day-number {
          font-size: 24px;
          font-weight: 600;
          margin-top: 4px;
        }

        .today-highlight {
          background-color: var(--primary);
          color: white!important;
          width: 40px;
          height: 40px;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .time-row {
          display: grid;
          grid-template-columns: 80px 1fr;
          border-bottom: 1px solid var(--light-gray);
        }

        .time-row:last-child {
          border-bottom: none;
        }

        .time-label {
          padding: 12px;
          font-size: 12px;
          font-weight: 500;
          color: var(--gray);
          text-align: right;
          padding-right: 16px;
        }

        .time-slot-cell {
          padding: 8px;
          min-height: 60px;
          border-left: 1px solid var(--light-gray);
          background-color: var(--background);
          transition: background-color 0.2s;
        }

        .time-slot-cell:hover {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
        }

        .appointment-item {
          background-color: color-mix(in srgb, var(--primary) 10%, transparent);
          border-left: 3px solid var(--primary);
          border-radius: 4px;
          padding: 8px;
          margin-bottom: 4px;
          height: 70%;
        }

        .appointment-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .appointment-time {
          font-size: 12px;
          font-weight: 500;
          color: var(--primary);
        }

        .appointment-badge {
          font-size: 12px;
          background-color: color-mix(in srgb, var(--primary) 20%, transparent);
          color: var(--primary);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .appointment-client {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          margin-top: 4px;
        }

        /* Week View Styles */
        .week-view {
          min-width: 800px;
        }

        .week-header-grid {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          border-bottom: 1px solid var(--light-gray);
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .week-header-cell {
          padding: 12px;
          text-align: center;
          border-left: 1px solid var(--light-gray);
        }

        .weekend-cell {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 50%,
            transparent
          );
        }

        .week-day-name {
          font-size: 12px;
          font-weight: 500;
          color: var(--gray);
          text-transform: uppercase;
        }

        .week-day-number {
          font-size: 18px;
          font-weight: 600;
          margin-top: 4px;
        }

        .weekend-text {
          color: var(--gray);
        }

        .week-time-row {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          border-bottom: 1px solid var(--light-gray);
        }

        .week-time-row:last-child {
          border-bottom: none;
        }

        .week-slot-cell {
          padding: 4px;
          min-height: 60px;
          border-left: 1px solid var(--light-gray);
          transition: background-color 0.2s;
        }

        .week-slot-cell:hover {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
        }

        .weekend-slot {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 20%,
            transparent
          );
        }

        .week-appointment-item {
          background-color: color-mix(in srgb, var(--primary) 10%, transparent);
          border-left: 3px solid var(--primary);
          border-radius: 4px;
          padding: 6px;
          margin-bottom: 4px;
          font-size: 12px;
          height: 80%;
        }

        .week-appointment-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .week-appointment-time {
          font-weight: 500;
          color: var(--primary);
        }

        .week-appointment-badge {
          background-color: color-mix(in srgb, var(--primary) 20%, transparent);
          color: var(--primary);
          padding: 2px 4px;
          border-radius: 4px;
          font-size: 10px;
        }

        .week-appointment-client {
          font-weight: 500;
          color: var(--text);
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Month View Styles */
        .month-view-container {
          padding: 16px;
        }

        .month-day-names {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          margin-bottom: 8px;
        }

        .month-day-name {
          padding: 8px;
          text-align: center;
          font-size: 12px;
          font-weight: 500;
          color: var(--gray);
          text-transform: uppercase;
        }

        .month-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          border: 1px solid var(--light-gray);
          border-radius: 8px;
          overflow: hidden;
        }

        .month-day-cell {
          min-height: 100px;
          padding: 8px;
          border-bottom: 1px solid var(--light-gray);
          border-right: 1px solid var(--light-gray);
        }

        .month-day-cell:last-child {
          border-right: none;
        }

        .month-day-cell:nth-child(7n) {
          border-right: none;
        }

        .month-day-cell:last-child,
        .month-day-cell:nth-last-child(-n + 7) {
          border-bottom: none;
        }

        .empty-day {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
        }

        .weekend-day {
          background-color: color-mix(
            in srgb,
            var(--light-gray) 20%,
            transparent
          );
        }

        .today-day {
          background-color: color-mix(in srgb, var(--primary) 5%, transparent);
        }

        .month-day-number {
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .today-highlight-month {
          background-color: var(--primary);
          color: white;
          width: 28px;
          height: 28px;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .current-month-day {
          color: var(--text);
        }

        .other-month-day {
          color: var(--gray);
        }

        .month-appointments {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .month-appointment-item {
          background-color: color-mix(in srgb, var(--primary) 10%, transparent);
          border-left: 3px solid var(--primary);
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .month-appointment-time {
          font-weight: 500;
          color: var(--primary);
        }

        .month-appointment-client {
          color: var(--text);
        }

        .more-appointments {
          font-size: 10px;
          color: var(--gray);
          padding-left: 6px;
        }

        /* Footer Styles */
        .calendar-footer {
          padding: 8px 16px;
          border-top: 1px solid var(--light-gray);
          background-color: color-mix(
            in srgb,
            var(--light-gray) 30%,
            transparent
          );
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
      `}</style>
    </div>
  );
}
